import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { ProjectType } from "reedy-data/models";
import { useSession } from "contexts/AuthContext";
import { useProduct } from "contexts/ProductContext";
import { useProjects } from "pages/Projects/contexts/ProjectsContext";
import { toast } from "utils/toast";
import { logError } from "shared/services/ErrorReporting";
import { useDialog } from "hooks/useDialog";
import { Modal } from "components/modal/Modal";
import { Button } from "components/button";
import { Text } from "components/text";
import {
  AddToProjectPicker,
  PickerProject,
} from "components/AddToProjectPicker";
import { newProjectNavigation } from "~/pages/Projects/newProjectNavigation";

interface PendingShared {
  project: PickerProject;
  nextIds: string[];
}

/**
 * Wires the reusable AddToProjectPicker to the book currently open on the
 * ProductDetails page and owns the assignment, shared-warning, and create-new
 * flows.
 */
export const ProductDetailsAddToProject = () => {
  "use no memo";

  const { t } = useTranslation(["productDetails", "general"]);
  const { product } = useProduct();
  const { organisation, user } = useSession();
  const { createProject, updateProjectIds } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();

  const pickerDialog = useDialog("add-to-project-picker");
  const createDialog = useDialog("create-project-with-book");
  const [pendingShared, setPendingShared] = useState<PendingShared | null>(
    null,
  );
  const [isBusy, setIsBusy] = useState(false);

  // useDialog returns a new object each render, but its methods are stable.
  const commitAssign = useCallback(
    async (project: PickerProject, nextIds: string[]) => {
      setIsBusy(true);
      try {
        await updateProjectIds(project.id, nextIds);
        toast.success(
          t("productDetails:addToProject.success", { project: project.name }),
        );
        pickerDialog.close();
      } catch (error) {
        logError("Failed to add book to project", { originalException: error });
        toast.error(t("productDetails:addToProject.error"));
      } finally {
        setIsBusy(false);
        setPendingShared(null);
      }
    },
    [updateProjectIds, t, pickerDialog.close],
  );

  const handleAssign = useCallback(
    (project: PickerProject) => {
      if (isBusy) {
        return;
      }

      const productId = product?.id;
      if (!productId) {
        return;
      }
      const nextIds = Array.from(new Set([...project.productIds, productId]));

      if (project.isShared) {
        setPendingShared({ project, nextIds });
        return;
      }
      commitAssign(project, nextIds);
    },
    [product?.id, commitAssign, isBusy],
  );

  const handleAlreadyAssigned = useCallback(
    (project: PickerProject) => {
      toast.info(
        t("productDetails:addToProject.alreadyAssigned", {
          project: project.name,
        }),
      );
    },
    [t],
  );

  const handleCreateNew = useCallback(() => {
    pickerDialog.close();
    createDialog.open();
  }, [pickerDialog.close, createDialog.open]);

  const confirmCreate = useCallback(async () => {
    const productId = product?.id;
    if (!productId) {
      return;
    }
    setIsBusy(true);
    try {
      const newProjectId = await createProject(
        t("general:newProjectName"),
        [productId],
        ProjectType.ISBNS,
      );
      if (newProjectId) {
        toast.success(t("productDetails:addToProject.created"));
        createDialog.close();
        navigate(newProjectNavigation(newProjectId, location.href));
      }
    } catch (error) {
      logError("Failed to create project for book", {
        originalException: error,
      });
      toast.error(t("productDetails:addToProject.error"));
    } finally {
      setIsBusy(false);
    }
  }, [product?.id, createProject, navigate, location, t, createDialog.close]);

  const handlePickerOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !pendingShared) {
        pickerDialog.close();
      }
    },
    [pendingShared, pickerDialog.close],
  );

  if (!product) {
    return null;
  }

  const pickerVisible = pickerDialog.isOpen && !pendingShared;

  return (
    <>
      <Button
        variant="tertiary"
        size="small"
        icon="folder-plus"
        iconPosition="left"
        label={t("productDetails:addToProject.button")}
        onClick={pickerDialog.open}
        analyticsId="button_add_book_to_project"
      />

      <AddToProjectPicker
        open={pickerVisible}
        onOpenChange={handlePickerOpenChange}
        header={
          <Text as="h2" variant="title">
            {t("productDetails:addToProject.header", {
              title: product.title ?? "",
            })}
          </Text>
        }
        organisationId={organisation.id}
        userId={user.id}
        productId={product.id}
        onAssign={handleAssign}
        onAlreadyAssigned={handleAlreadyAssigned}
        onCreateNew={handleCreateNew}
        isAssigning={isBusy}
      />

      <Modal
        id="add-to-shared-project-warning"
        open={Boolean(pendingShared)}
        variant="warning"
        loading={isBusy}
        title={t("productDetails:addToProject.sharedWarning.title")}
        description={t(
          "productDetails:addToProject.sharedWarning.description",
          {
            project: pendingShared?.project.name ?? "",
            owner:
              pendingShared?.project.ownerName ??
              t("productDetails:addToProject.sharedWarning.unknownOwner"),
            book: product.title ?? "",
          },
        )}
        confirmLabel={t("productDetails:addToProject.sharedWarning.confirm")}
        cancelLabel={t("general:buttons.cancel")}
        onOpenChange={(open) => {
          if (!open && !isBusy) {
            setPendingShared(null);
          }
        }}
        onConfirm={() => {
          if (pendingShared && !isBusy) {
            commitAssign(pendingShared.project, pendingShared.nextIds);
          }
        }}
      />

      <Modal
        id="create-project-with-book"
        open={createDialog.isOpen}
        variant="warning"
        loading={isBusy}
        title={t("productDetails:addToProject.create.title")}
        description={t("productDetails:addToProject.create.description", {
          book: product.title ?? "",
        })}
        confirmLabel={t("productDetails:addToProject.create.confirm")}
        cancelLabel={t("general:buttons.cancel")}
        onOpenChange={createDialog.onOpenChange}
        onConfirm={confirmCreate}
      />
    </>
  );
};
